import React from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './app';
import { initTelemetry } from './telemetry/init';

initTelemetry({
  endpoint: (import.meta.env.VITE_OTEL_ENDPOINT as string | undefined),
  serviceName: 'astroline-widget',
  enabled: import.meta.env.VITE_TELEMETRY_ENABLED !== 'false',
});

const root = document.getElementById('root');
if (!root) throw new Error('root element missing');

// F2-I3: location-based routing for the 4 Sigma widget mount points.
// `packages/extension/plugin-manifest.json` declares 4 widgets, each pointing
// at a distinct path under `https://widget-zohodesk.kunzite.app/zoho/<surface>`:
//   - /zoho/bottombar     -> desk.bottombar              (full softphone, App)
//   - /zoho/topband       -> desk.topband                (full softphone, App)
//   - /zoho/leftpanel     -> desk.ticket.detail.leftpanel(call history)
//   - /zoho/settings      -> settings (config iframe)    (admin disposition codes)
//
// The bottombar is the "primary" surface that brings up the SIP UA, WS, and
// services boot. The leftpanel and settings surfaces consume that boot via
// React context, so they need a shared services boot to be useful in
// production.
//
// F2 deuda — production wire-up:
//   The components rendered for `leftpanel` and `settings` (TicketDetailPane,
//   SettingsPage) call `useWidgetServices()` and therefore require a
//   `WidgetServicesProvider`. The provider is currently populated by the boot
//   sequence inside `App` (auth-exchange + SIP UA + WS dispatcher), which is
//   too heavy for these surfaces (settings doesn't need SIP, leftpanel doesn't
//   need WS). F3 will:
//     1. Refactor the boot in `app.tsx` into a `bootServices()` async function
//        with options to skip SIP/WS for non-call surfaces.
//     2. Share the resulting WidgetServices across iframes (Sigma's iframe
//        model means each location is a separate iframe; postMessage or a
//        per-location lighter-weight boot are the two real options).
//     3. Wire `getTicket: () => ZOHODESK.get('ticket')` into TicketDetailPane.
//
// For F2 we ship the routing scaffold and a placeholder that surfaces the
// deuda inline — the components themselves are unit-tested in I1/I2/H4/H5.
const surface = detectSurface();
createRoot(root).render(
  <React.StrictMode>
    {surface === 'leftpanel' ? (
      <TicketDetailRoot />
    ) : surface === 'settings' ? (
      <SettingsRoot />
    ) : (
      // bottombar + topband both render the full softphone App for now.
      // F3 may split topband into a status-only surface.
      <App />
    )}
  </React.StrictMode>,
);

type Surface = 'bottombar' | 'topband' | 'leftpanel' | 'settings';

function detectSurface(): Surface {
  // Primary signal: pathname suffix (matches the manifest URLs above).
  // Fallback signal: ?loc=<surface> query param (useful for local dev / tests).
  const path = window.location.pathname;
  if (path.endsWith('/leftpanel') || path.includes('/leftpanel')) return 'leftpanel';
  if (path.endsWith('/settings') || path.includes('/settings')) return 'settings';
  if (path.endsWith('/topband') || path.includes('/topband')) return 'topband';

  const params = new URLSearchParams(window.location.search);
  const loc = params.get('loc');
  if (loc === 'leftpanel' || loc === 'ticket-detail') return 'leftpanel';
  if (loc === 'settings') return 'settings';
  if (loc === 'topband') return 'topband';
  return 'bottombar';
}

function TicketDetailRoot(): React.JSX.Element {
  // F2-I3: pending real shared-boot integration. The production wire-up would:
  //   1. await bootServices({ withSip: false, withWs: true }).
  //   2. Wrap <TicketDetailPane getTicket={() => ZOHODESK.get('ticket')} />
  //      inside <WidgetServicesProvider services={...}>.
  // For F2 we render a placeholder that documents the deuda without
  // half-wiring something that crashes when CallHistoryPanel calls
  // services.calls.findByContact().
  return (
    <main style={{ padding: 16, fontFamily: 'system-ui, sans-serif' }}>
      <h2>Historial de llamadas</h2>
      <p style={{ color: '#666', fontSize: 13 }}>
        F2 deuda: integración con shared services boot pendiente. La UI
        (TicketDetailPane + CallHistoryPanel) está implementada y testeada
        en I1/I2; F3 conectará el boot de servicios a esta superficie.
      </p>
    </main>
  );
}

function SettingsRoot(): React.JSX.Element {
  // F2-I3: same deuda — SettingsPage hosts DispositionCodesEditor which calls
  // services.tenants.getDispositionCodes() / setDispositionCodes(). Until the
  // shared-boot lands, we render a placeholder to surface the deuda inline.
  return (
    <main style={{ padding: 16, fontFamily: 'system-ui, sans-serif' }}>
      <h1>Kunzite · Settings</h1>
      <p style={{ color: '#666', fontSize: 13 }}>
        F2 deuda: integración con shared services boot pendiente. La UI
        (SettingsPage + DispositionCodesEditor) está implementada y testeada
        en H4/H5; F3 conectará el boot de servicios a esta superficie.
      </p>
    </main>
  );
}
